import React from "react";
import PageTitle from "@cs/components/src/PageTitle";
import Button from "@cs/components/src/Button";
import { AlertTriangle, Plus, RotateCcw, Search } from "react-feather";
import Table from "@cs/components/src/Table";
import Section from "@cs/components/src/Section";
import Spinner from "@cs/components/src/Spinner";
import { useMutation, useQuery } from "react-query";
import ApiService from "../../services/ApiService";
import styled from "styled-components";
import { PageWrapper } from "@cs/components/src/PageWrapper";
import { formatNumber } from "../shared/utils";
import Fuse from "fuse.js";
import Input from "@cs/components/src/Input";
import { useOrganizationAuthCheck } from "../../hooks/useOrganizationAuthCheck";
import Tooltip from "@cs/components/src/Tooltip";
import { useUpdateComponentsJob } from "../../context/UpdateComponentsJobContext";

const Tag = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 3px 5px;
  border-radius: 2px;
  margin-right: 5px;
  margin-top: 2.5px;
  margin-bottom: 2.5px;
  background-color: var(--dark400);
  color: var(--gray200);
  margin-left: 15px;
`;

const Count = styled.span<{ $empty: boolean; $large?: boolean }>`
  color: ${(props) => (props.$empty ? "var(--gray100)" : "var(--gray500)")};
  font-weight: ${(props) => (props.$large ? "bold" : "normal")};
`;

const StaleBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 3px 6px;
  border-radius: 2px;
  margin-left: 8px;
  background-color: var(--red100);
  color: var(--gray500);
  font-size: 12px;
`;

const ConfigureOrganizationListScreen = () => {
  const { addComponentsJob } = useUpdateComponentsJob();
  const updateAllMutation = useMutation({
    mutationFn: () =>
      ApiService.fetch<{ scriptExecutionId: string; systemDomainId: string }>(
        "POST",
        `/api/plugin_instance/update_all`
      ),
    onSuccess: (data) =>
      addComponentsJob(
        data.scriptExecutionId,
        data.systemDomainId,
        "Updating components"
      ),
  });

  const { data, isLoading, isError } = useQuery({
    queryKey: ["organizationList"],
    queryFn: async (): Promise<OrganizationInfo[]> => {
      const response = await ApiService.fetch<{ data: OrganizationInfo[] }>(
        "GET",
        `/api/config/organization`
      );
      return response.data;
    },
  });
  const { data: authStatus } = useOrganizationAuthCheck();

  const [search, setSearch] = React.useState("");

  let rows = data ?? [];
  if (search) {
    const fuse = new Fuse<OrganizationInfo>(rows ?? [], {
      keys: ["title", "organizations"],
      ignoreLocation: true,
      threshold: 0.2,
      shouldSort: false,
    });

    rows = fuse.search(search).map((result) => result.item);
  }

  const now = new Date();
  const prev = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const twoAgo = new Date(now.getFullYear(), now.getMonth() - 2, 1);

  const currentMonth = now.toLocaleDateString("en-US", { month: "short" });
  const previousMonth = prev.toLocaleDateString("en-US", { month: "short" });
  const twoAgoMonth = twoAgo.toLocaleDateString("en-US", { month: "short" });

  return (
    <div>
      <PageTitle
        title="Organizations"
        error={isError ? "Problem loading organizations" : undefined}
      >
        <Input
          value={search}
          onChange={setSearch}
          placeholder="Search"
          icon={Search}
          clearable
          width={200}
        />
        <Button
          label="Update All Organizations"
          icon={RotateCcw}
          type="default"
          borderless
          onClick={() => updateAllMutation.mutate()}
          loading={updateAllMutation.isLoading}
        />
        <Button
          label="Add Organization"
          icon={Plus}
          link="/config/organization/new"
          type="success"
        />
      </PageTitle>

      {isLoading ? (
        <Spinner />
      ) : (
        !isError && (
          <PageWrapper>
            <Section>
              <Table
                columnGrid
                columns={[
                  { title: "Name", width: "2fr" },
                  { width: "50px", title: twoAgoMonth },
                  { width: "50px", title: previousMonth },
                  { width: "50px", title: currentMonth },
                ]}
                rowHeight={40}
                rows={
                  rows.map((organization) => {
                    const auth = authStatus?.[organization.systemDomainId];
                    return {
                      id: organization.systemDomainId,
                      element: () => (
                        <>
                          <span>
                            {organization.title}
                            <Tag>{organization.systemDomainId}</Tag>
                            {auth && !auth.valid && (
                              <Tooltip
                                label={auth.error ?? "Authentication failed"}
                                type="danger"
                              >
                                <StaleBadge>
                                  <AlertTriangle size={12} />
                                  Stale token
                                </StaleBadge>
                              </Tooltip>
                            )}
                          </span>
                          {organization.counts?.map((count, index) => (
                            <Count
                              key={index}
                              $empty={!count}
                              $large={count > 1000000}
                            >
                              {formatNumber(count) ?? "-"}
                            </Count>
                          ))}
                        </>
                      ),
                      link: organization.systemDomainId,
                    };
                  }) ?? []
                }
                autoHeight={{}}
              />
            </Section>
          </PageWrapper>
        )
      )}
    </div>
  );
};

export default ConfigureOrganizationListScreen;
